"use client";

import React, { useState, useMemo } from "react";
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Terminal,
  Sparkles,
  FileText,
  ArrowRight,
  RefreshCw,
} from "lucide-react";

interface PresetScenario {
  id: string;
  label: string;
  badge: string;
  sampleSlug: string;
  text: string;
}

const PRESETS: PresetScenario[] = [
  {
    id: "high-gain",
    label: "1. Praxis-Messreihe & Post-Mortem (Hoher Information Gain)",
    badge: "Empfohlen",
    sampleSlug: "geo",
    text: `Beim Praxistest von 48 registrierten MCP-Tools in Claude Desktop (v0.8.2) sank die korrekte Tool-Auswahl ab dem 23. Schema um 64 %, weil die JSON-Schema-Definitionen bereits 14.200 Tokens des Kontextfensters belegten. Auf iOS 18 Safari führte das parallele Laden eines 1,8 GB großen Q4-Modells in WebGPU nach 11 Sekunden deterministisch zu einem Out-of-Memory-Abbruch des Service Workers.

| Transport-Modus | Mittlere Latenz (p95) | Token-Overhead pro Seite | Typischer Fehlergrund |
| :--- | :--- | :--- | :--- |
| Vollständiges SSR-HTML | 185 ms | 11.420 Tokens | Hydration-Payloads & Inline-SVGs |
| Accept: text/markdown | 24 ms | 1.680 Tokens | Fehlt bei Client-Only-SPAs |

Grenze: Reine Markdown-Endpunkte helfen nur bei Retrieval-Crawlern, die Content-Negotiation unterstützen oder explizit .md-URLs aus llms.txt abrufen (wie Claude Code oder Perplexity-User), ersetzen aber niemals reguläres HTML für den Googlebot.`,
  },
  {
    id: "low-gain",
    label: "2. Generische Zusammenfassung & Buzzwords (Kein Information Gain)",
    badge: "Negativbeispiel",
    sampleSlug: "web-mcp",
    text: `Was ist Generative Engine Optimization? In der heutigen schnelllebigen digitalen Welt verändert künstliche Intelligenz die Suche revolutionär. Unternehmen benötigen eine nahtlose Integration und blazing fast Webseiten, um mit einer cutting-edge Strategie zukunftssicher und enterprise-ready aufgestellt zu sein.

Moderne KI-Suchmaschinen nutzen große Sprachmodelle, um Antworten für Nutzer zusammenzufassen. Wer seine Webseite optimal strukturiert und qualitativ hochwertigen Content veröffentlicht, profitiert von maximaler Sichtbarkeit und nachhaltigem Wachstum auf allen Kanälen.`,
  },
  {
    id: "mixed-guide",
    label: "3. Solide Anleitung ohne Grenzwerte (Mittlerer Information Gain)",
    badge: "Ausbaufähig",
    sampleSlug: "brownfield-ai",
    text: `Um eine Next.js-16-Anwendung für KI-Agenten bereitzustellen, leitest du Anfragen mit dem HTTP-Header Accept: text/markdown in src/proxy.ts auf einen dedizierten Route-Handler um. Dadurch erhalten Sprachmodelle den Artikeltext ohne Navigationsmenüs und Skript-Tags.

1. Erstelle die Datei src/proxy.ts im Projektstamm.
2. Prüfe im Request-Header, ob text/markdown vor text/html priorisiert wird.
3. Liefere den bereinigten MDX-Inhalt mit Content-Type: text/markdown; charset=utf-8 aus.`,
  },
];

const BANNED_FLOSKELN = [
  "blazing fast",
  "seamless",
  "nahtlose integration",
  "nahtlos",
  "enterprise-ready",
  "cutting-edge",
  "revolutionär",
  "revolutionary",
  "game-changer",
  "zukunftssicher",
  "schnelllebigen digitalen welt",
];

// Measured average Next.js SSR HTML layout + RSC hydration shell overhead on lukiluki.me
const SSR_SHELL_OVERHEAD_CHARS = 38400;

interface LiveFetchResult {
  slug: string;
  status: number;
  contentType: string;
  negotiationHeader: string;
  tokensHeader: string;
  linkHeader: string;
  previewText: string;
  totalBytes: number;
  fetchedAt: string;
}

export default function GeoAuditorPlayground() {
  const [selectedPresetId, setSelectedPresetId] = useState<string>(PRESETS[0].id);
  const [inputText, setInputText] = useState<string>(PRESETS[0].text);
  const [targetSlug, setTargetSlug] = useState<string>(PRESETS[0].sampleSlug);
  const [isEditedSincePreset, setIsEditedSincePreset] = useState<boolean>(false);

  const [isFetchingLive, setIsFetchingLive] = useState<boolean>(false);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [liveResult, setLiveResult] = useState<LiveFetchResult | null>(null);
  const [liveResultStale, setLiveResultStale] = useState<boolean>(false);

  const handleSelectPreset = (preset: PresetScenario) => {
    setSelectedPresetId(preset.id);
    setInputText(preset.text);
    setTargetSlug(preset.sampleSlug);
    setIsEditedSincePreset(false);
    if (liveResult && liveResult.slug !== preset.sampleSlug) {
      setLiveResultStale(true);
    }
  };

  const handleTextChange = (val: string) => {
    setInputText(val);
    setIsEditedSincePreset(true);
  };

  const audit = useMemo(() => {
    const trimmed = inputText.trim();
    const charCount = trimmed.length;
    const wordCount = trimmed ? trimmed.split(/\s+/).length : 0;

    // 1. Quantitative metrics (numbers with units: %, ms, s, KB, MB, GB, Tokens, v0.x)
    const metricMatches =
      trimmed.match(
        /\b\d+(?:[.,]\d+)?\s*(?:%|ms|s|Sekunden|KB|MB|GB|Tokens|Tools|Zeilen|req\/s|p95|p99)\b/gi
      ) || [];

    // 2. Firsthand experience & explicit boundaries
    const boundaryMatches =
      trimmed.match(
        /\b(?:Praxistest|Messung|Fehler|Abbruch|Out-of-Memory|Grenze|Limitation|ersetzt\s+nicht|Fehlergrund|Debugging|Safari|Chrome|v\d+\.\d+)\b/gi
      ) || [];

    // 3. Structural extractability (Markdown tables, numbered steps, code blocks)
    const hasTable = /\|[^\n]+\|\s*\n\s*\|[\s:-]+\|/.test(trimmed);
    const hasListOrSteps = /(?:^|\n)\s*(?:\d+\.|[-*])\s+\S+/.test(trimmed);

    // 4. Anti-Floskel check
    const lower = trimmed.toLowerCase();
    const foundFloskeln = BANNED_FLOSKELN.filter((f) => lower.includes(f));

    // 5. Token economy calculation (Heuristic: ~4 chars per token in German/English technical prose)
    const mdTokens = Math.max(1, Math.ceil((charCount + 380) / 4));
    const htmlChars = SSR_SHELL_OVERHEAD_CHARS + Math.round(charCount * 2.4);
    const htmlTokens = Math.ceil(htmlChars / 4);
    const tokenSavingsPercent = Math.round(
      ((htmlTokens - mdTokens) / htmlTokens) * 100
    );
    const signalRatioMd = Math.min(
      98,
      Math.round((charCount / Math.max(1, charCount + 380)) * 100)
    );
    const signalRatioHtml = Math.max(
      2,
      Math.round((charCount / Math.max(1, htmlChars)) * 100)
    );

    // Overall Information Gain Score (0 - 100) — heuristic model
    let score = 20;
    score += Math.min(30, metricMatches.length * 10);
    score += Math.min(25, boundaryMatches.length * 8);
    if (hasTable) score += 15;
    if (hasListOrSteps) score += 10;
    score -= foundFloskeln.length * 15;
    score = Math.max(0, Math.min(100, score));

    return {
      charCount,
      wordCount,
      metricMatches,
      boundaryMatches,
      hasTable,
      hasListOrSteps,
      foundFloskeln,
      mdTokens,
      htmlTokens,
      tokenSavingsPercent,
      signalRatioMd,
      signalRatioHtml,
      score,
    };
  }, [inputText]);

  const handleTestLiveEndpoint = async () => {
    setIsFetchingLive(true);
    setLiveError(null);
    try {
      const response = await fetch(`/garden/${targetSlug}.md`, {
        headers: {
          Accept: "text/markdown",
        },
      });
      const text = await response.text();
      setLiveResult({
        slug: targetSlug,
        status: response.status,
        contentType: response.headers.get("content-type") || "unknown",
        negotiationHeader:
          response.headers.get("x-content-negotiation") || "text/markdown",
        tokensHeader:
          response.headers.get("x-markdown-tokens-est") ||
          String(Math.ceil(text.length / 4)),
        linkHeader: response.headers.get("link") || "–",
        previewText: text.slice(0, 620),
        totalBytes: new Blob([text]).size,
        fetchedAt: new Date().toLocaleTimeString("de-DE"),
      });
      setLiveResultStale(false);
    } catch (err) {
      setLiveError(
        err instanceof Error
          ? err.message
          : "Netzwerkfehler beim Abruf des Markdown-Endpunkts."
      );
    } finally {
      setIsFetchingLive(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 min-h-[580px]">
      {/* Top Preset Selector */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
            Szenario wählen oder eigenen Abschnitt prüfen (Heuristische Analyse)
          </span>
          {isEditedSincePreset && (
            <span className="rounded-full bg-amber-500/10 border border-amber-500/30 px-2.5 py-0.5 font-mono text-[11px] font-medium text-amber-700 dark:text-amber-300">
              Eigene Textänderung aktiv
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {PRESETS.map((preset) => {
            const active = selectedPresetId === preset.id && !isEditedSincePreset;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => handleSelectPreset(preset)}
                className={`min-h-[44px] flex flex-col justify-between rounded-xl border p-3 text-left transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-purple-500 ${
                  active
                    ? "border-purple-500 bg-purple-500/10 text-neutral-900 dark:text-white shadow-2xs"
                    : "border-neutral-200 bg-white hover:border-purple-400/60 dark:border-neutral-800 dark:bg-neutral-900/70 dark:text-neutral-200"
                }`}
              >
                <span className="text-xs font-semibold leading-snug">
                  {preset.label}
                </span>
                <span className="mt-1.5 inline-flex w-fit rounded-md bg-neutral-100 px-1.5 py-0.5 font-mono text-[10px] font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                  {preset.badge}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Split Grid: Editor Left, Telemetry & Signals Right */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        {/* Left Column: Text Input */}
        <div className="flex flex-col gap-2 lg:col-span-6">
          <label
            htmlFor="geo-auditor-textarea"
            className="flex items-center justify-between text-xs font-semibold text-neutral-700 dark:text-neutral-300"
          >
            <span>Zu prüfender Artikelabschnitt (Markdown / Plaintext)</span>
            <span className="font-mono text-[11px] text-neutral-500">
              {audit.wordCount} Wörter · {audit.charCount} Zeichen
            </span>
          </label>
          <textarea
            id="geo-auditor-textarea"
            value={inputText}
            onChange={(e) => handleTextChange(e.target.value)}
            rows={10}
            className="w-full rounded-xl border border-neutral-200 bg-neutral-50/70 p-3.5 font-mono text-xs leading-relaxed text-neutral-800 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-purple-500 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-200"
            placeholder="Füge hier deinen Fachtext, Messwerte oder eine Markdown-Tabelle ein..."
          />
          {audit.charCount < 40 && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Hinweis: Gib mindestens 40 Zeichen ein, um eine belastbare Signal-Auswertung zu erhalten.
            </p>
          )}
        </div>

        {/* Right Column: Deterministic Signal Evaluation */}
        <div className="flex flex-col justify-between gap-4 rounded-xl border border-neutral-200 bg-neutral-50/50 p-4 dark:border-neutral-800 dark:bg-neutral-900/50 lg:col-span-6">
          {/* Score & Token Comparison Bar */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between border-b border-neutral-200/80 pb-3 dark:border-neutral-800">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                  Information-Gain-Index (Heuristik)
                </div>
                <div className="mt-0.5 text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  {audit.score >= 75
                    ? "Hohe Zitierfähigkeit (Primärdaten & Struktur)"
                    : audit.score >= 45
                      ? "Solide Struktur, aber wenig empirische Belege"
                      : "Geringer Information Gain (Redundanz-/Floskel-Risiko)"}
                </div>
              </div>
              <div
                className={`flex h-12 w-16 shrink-0 items-center justify-center rounded-xl border font-mono text-lg font-black ${
                  audit.score >= 75
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                    : audit.score >= 45
                      ? "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400"
                      : "border-rose-500/40 bg-rose-500/10 text-rose-600 dark:text-rose-400"
                }`}
              >
                {audit.score}/100
              </div>
            </div>

            {/* Token Economy Comparison */}
            <div className="grid grid-cols-2 gap-2.5">
              <div className="rounded-lg border border-neutral-200 bg-white p-2.5 dark:border-neutral-800 dark:bg-neutral-950">
                <div className="font-mono text-[10px] uppercase text-neutral-500">
                  SSR HTML-Dom (~38,4 KB Shell)
                </div>
                <div className="mt-1 font-mono text-sm font-bold text-neutral-800 dark:text-neutral-200">
                  ~{audit.htmlTokens.toLocaleString("de-DE")} Tokens
                </div>
                <div className="mt-0.5 text-[11px] text-neutral-500">
                  Signal-Anteil: nur {audit.signalRatioHtml} % Nutztext
                </div>
              </div>

              <div className="rounded-lg border border-purple-500/30 bg-purple-500/5 p-2.5 dark:bg-purple-500/10">
                <div className="font-mono text-[10px] uppercase text-purple-600 dark:text-purple-300">
                  Accept: text/markdown (.md)
                </div>
                <div className="mt-1 font-mono text-sm font-bold text-purple-700 dark:text-purple-200">
                  ~{audit.mdTokens.toLocaleString("de-DE")} Tokens (-{audit.tokenSavingsPercent} %)
                </div>
                <div className="mt-0.5 text-[11px] text-purple-600/90 dark:text-purple-300/90">
                  Signal-Anteil: {audit.signalRatioMd} % Nutztext
                </div>
              </div>
            </div>
          </div>

          {/* 4 Signal Checks */}
          <div className="flex flex-col gap-2 text-xs">
            <div className="flex items-start justify-between gap-2 rounded-lg bg-white px-3 py-2 dark:bg-neutral-950">
              <div className="flex items-center gap-2">
                {audit.metricMatches.length >= 2 ? (
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                ) : (
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
                )}
                <span className="font-medium text-neutral-800 dark:text-neutral-200">
                  Messwerte & Einheiten ({audit.metricMatches.length} erkannt)
                </span>
              </div>
              <span className="font-mono text-[11px] text-neutral-500 truncate max-w-[180px]">
                {audit.metricMatches.length > 0
                  ? audit.metricMatches.slice(0, 3).join(", ")
                  : "Keine Zahlenbelege"}
              </span>
            </div>

            <div className="flex items-start justify-between gap-2 rounded-lg bg-white px-3 py-2 dark:bg-neutral-950">
              <div className="flex items-center gap-2">
                {audit.boundaryMatches.length >= 2 ? (
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                ) : (
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
                )}
                <span className="font-medium text-neutral-800 dark:text-neutral-200">
                  Praxis-/Grenzsignale ({audit.boundaryMatches.length} erkannt)
                </span>
              </div>
              <span className="font-mono text-[11px] text-neutral-500 truncate max-w-[180px]">
                {audit.boundaryMatches.length > 0
                  ? audit.boundaryMatches.slice(0, 3).join(", ")
                  : "Keine Fehler-/Grenzangaben"}
              </span>
            </div>

            <div className="flex items-start justify-between gap-2 rounded-lg bg-white px-3 py-2 dark:bg-neutral-950">
              <div className="flex items-center gap-2">
                {audit.hasTable || audit.hasListOrSteps ? (
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                ) : (
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
                )}
                <span className="font-medium text-neutral-800 dark:text-neutral-200">
                  Maschinenlesbare Struktur (Tabelle / Schritte)
                </span>
              </div>
              <span className="font-mono text-[11px] text-neutral-500">
                {audit.hasTable
                  ? "Markdown-Tabelle aktiv"
                  : audit.hasListOrSteps
                    ? "Schritte-Liste aktiv"
                    : "Nur Fließtext"}
              </span>
            </div>

            <div className="flex items-start justify-between gap-2 rounded-lg bg-white px-3 py-2 dark:bg-neutral-950">
              <div className="flex items-center gap-2">
                {audit.foundFloskeln.length === 0 ? (
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                ) : (
                  <XCircle className="h-4 w-4 shrink-0 text-rose-500" />
                )}
                <span className="font-medium text-neutral-800 dark:text-neutral-200">
                  Floskel-Filter (Evidenz statt Marketing)
                </span>
              </div>
              <span className="font-mono text-[11px] text-rose-600 dark:text-rose-400 truncate max-w-[180px]">
                {audit.foundFloskeln.length === 0
                  ? "0 Buzzwords (Clean)"
                  : audit.foundFloskeln.join(", ")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Live Content-Negotiation Inspector */}
      <div className="rounded-xl border border-neutral-200 bg-neutral-900 p-4 text-neutral-100 dark:border-neutral-800">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-800 pb-3">
          <div className="flex items-center gap-2">
            <Terminal className="h-4 w-4 text-purple-400" />
            <span className="font-mono text-xs font-bold text-white">
              Live-Test: Next.js 16 Markdown Content-Negotiation auf lukiluki.me
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="live-slug-select" className="sr-only">
              Garden-Route für Live-Abruf auswählen
            </label>
            <select
              id="live-slug-select"
              value={targetSlug}
              onChange={(e) => {
                setTargetSlug(e.target.value);
                if (liveResult && liveResult.slug !== e.target.value) {
                  setLiveResultStale(true);
                }
              }}
              className="min-h-[44px] rounded-lg border border-neutral-700 bg-neutral-800 px-3 font-mono text-xs text-neutral-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-purple-500"
            >
              <option value="geo">/garden/geo.md</option>
              <option value="web-mcp">/garden/web-mcp.md</option>
              <option value="brownfield-ai">/garden/brownfield-ai.md</option>
              <option value="3d-print-mcp">/garden/3d-print-mcp.md</option>
              <option value="accessibility">/garden/accessibility.md</option>
            </select>

            <button
              type="button"
              onClick={handleTestLiveEndpoint}
              disabled={isFetchingLive}
              className="min-h-[44px] inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 font-mono text-xs font-semibold text-white transition-colors hover:bg-purple-500 disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-purple-400"
            >
              {isFetchingLive ? (
                <>
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  <span>Lade .md Stream...</span>
                </>
              ) : (
                <>
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>GET /garden/{targetSlug}.md ausführen</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Reserved Output Area (CLS=0) */}
        <div className="mt-3 min-h-[150px] flex flex-col justify-center">
          {liveError ? (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 font-mono text-xs text-rose-300">
              Fehler beim Live-Abruf: {liveError}
            </div>
          ) : liveResult ? (
            <div className="flex flex-col gap-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2 font-mono text-[11px]">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-emerald-500/20 px-2 py-0.5 text-emerald-300 font-bold">
                    HTTP {liveResult.status}
                  </span>
                  <span className="text-neutral-300">
                    Content-Type: <strong className="text-purple-300">{liveResult.contentType}</strong>
                  </span>
                  <span className="text-neutral-300">
                    Payload: <strong className="text-white">{(liveResult.totalBytes / 1024).toFixed(1)} KB</strong> (~{liveResult.tokensHeader} Tokens)
                  </span>
                </div>
                {liveResultStale ? (
                  <span className="rounded bg-amber-500/20 px-2 py-0.5 text-amber-300">
                    Ergebnis gehört noch zu /garden/{liveResult.slug}.md (bitte neu abrufen)
                  </span>
                ) : (
                  <span className="text-neutral-400">
                    Abgerufen um {liveResult.fetchedAt}
                  </span>
                )}
              </div>
              <pre className="max-h-[140px] overflow-y-auto rounded-lg border border-neutral-800 bg-neutral-950 p-3 font-mono text-[11px] leading-relaxed text-neutral-300 whitespace-pre-wrap">
                {liveResult.previewText}
                {"\n... [Gekürzte Vorschau des Live-Markdown-Streams]"}
              </pre>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-1.5 py-6 text-center font-mono text-xs text-neutral-400">
              <FileText className="h-5 w-5 text-neutral-500" />
              <span>
                Klicke auf „GET /garden/{targetSlug}.md ausführen“, um die echte Next.js-16-Proxy-Antwort inklusive HTTP-Headern live zu prüfen.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
